import { useToast } from "@mallardbay/lib-react-components"
import { useState } from "react"

import type {
    PaymentPlanType,
    QuoteFieldsFragment,
} from "~graphql/generated/graphql"
import {
    useQuoteQuery,
    useEnableQuotePaymentGroupMutation,
    useDisableQuotePaymentGroupMutation,
} from "~graphql/generated/graphql"
import type { PaymentMethodState } from "~utils/use-payment-method-state"

export default function useQuote({
    quoteId,
    paymentPlanType,
    paymentMethodState,
}: {
    quoteId: string | null | undefined
    paymentPlanType: PaymentPlanType
    paymentMethodState: PaymentMethodState
}) {
    const [quote, setQuote] = useState<QuoteFieldsFragment | null>(null)

    const { showError } = useToast()
    const { isAchPaymentMethod, isExternalPaymentMethod } = paymentMethodState

    const {
        loading: isLoading,
        error: quoteError,
        refetch,
    } = useQuoteQuery({
        variables: {
            id: quoteId ?? "",
            paymentPlanType,
            isAchPaymentMethod,
            isExternalPaymentMethod,
        },
        skip: !quoteId,
        // Using cache-and-network to avoid stale data and cache issues.
        // With the regular fetch policy, once each set of arguments are cached,
        // apollo just gives us the same quote instance each time.
        // Tried to use no-cache, but it prevented the refetching from working properly
        // when enabling/disabling group pay. Do not change this to no-cache.
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => setQuote(data.quote),
    })
    const refetchQuote = () => {
        void refetch()
    }

    const [enableGroupPay, { loading: isEnablingGroupPay }] =
        useEnableQuotePaymentGroupMutation({
            onError: showError,
            onCompleted: refetchQuote,
        })

    const [disableGroupPay, { loading: isDisablingGroupPay }] =
        useDisableQuotePaymentGroupMutation({
            variables: { quoteId: quoteId ?? "" },
            onError: showError,
            onCompleted: refetchQuote,
        })

    const listing = quote?.listing ?? null
    const isLoadingGroupPay = isEnablingGroupPay || isDisablingGroupPay
    const isLoadingQuote = isLoading || isLoadingGroupPay

    return {
        quote,
        listing,
        enableGroupPay,
        disableGroupPay,
        isLoadingQuote,
        quoteError,
        refetchQuote,
    }
}
